/** @format */

import {Box, Typography} from '@mui/material';
import {styled} from '@mui/material/styles';

import {scrollbarStyles} from 'constants/scrollbar';

// Outer container: border + rounded corners matching Figma node 10284:127902.
// border: 1px solid surface-2 (#4a4b4f), border-radius: 8px.
// width:fit-content + maxWidth:100% → the table shrinks to its content (shrink-to-fit), not
// the full card width. overflowX:auto — when content is wider than the card (too narrow to fit
// all 6 columns), scroll horizontally instead of clipping «Выполнение»/«Прогноз». overflowY:hidden
// keeps the rounded corners clipping the content. The horizontal scrollbar uses the systemic
// dark styling (same as the campaigns table / setup-preview blocks) instead of the OS default.
export const TableRoot = styled(Box)(({theme}) => ({
  border: `1px solid ${theme.palette.semantic_dark?.surface.surface2}`,
  borderRadius: 8,
  overflowX: 'auto',
  overflowY: 'hidden',
  width: 'fit-content',
  maxWidth: '100%',
  ...scrollbarStyles(theme, 'dark'),
}));

// CSS-grid wrapper: 6 columns, auto-sized to their content (shrink-to-fit). No minWidth — the
// grid keeps its content width so the bordered table is only as wide as its columns.
export const TableGrid = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(6, auto)',
  width: 'max-content',
}));

// Header cell — 14px/500/medium, onSurfaceLight, px-16 py-8.
export const HeaderCell = styled(Box)(({theme}) => ({
  padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  whiteSpace: 'nowrap',
}));

// Header text line (bold)
export const HeaderText = styled(Typography)(({theme}) => ({
  fontSize: 14,
  lineHeight: '16px',
  fontWeight: 500,
  color: theme.palette.semantic_dark?.surface.onSurfaceLight,
  whiteSpace: 'nowrap',
}));

// Header sub-line (regular weight — "на 20 апреля")
export const HeaderSubText = styled(Typography)(({theme}) => ({
  fontSize: 14,
  lineHeight: '16px',
  fontWeight: 400,
  color: theme.palette.semantic_dark?.surface.onSurfaceLight,
  whiteSpace: 'nowrap',
}));

// Header cell for Прогноз column — inline flex for icon + label.
// alignItems:flex-start top-aligns the icon+text row to match the column header cells (which
// top-align their text); centering would drop it below them in the stretched grid cell.
// Systemic rule: the inline icon's box equals the adjacent text's line-height (HeaderText = 16px),
// so it shares the text's line box — perfectly aligned with the text without making the row taller
// or pushing the text down. Encoded here (not per call-site) so every use stays consistent.
export const ForecastHeaderCell = styled(HeaderCell)(({theme}) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  '& .material-symbols-outlined': {
    fontSize: '16px',
    lineHeight: '16px',
  },
}));

// Row separator spanning all 6 columns
export const RowDivider = styled(Box)(({theme}) => ({
  gridColumn: '1 / span 6',
  height: 0,
  borderTop: `1px solid ${theme.palette.semantic_dark?.surface.surface2}`,
}));

// Data cell — 16px/400/regular, px-16 py-8, align-self stretch.
export const DataCell = styled(Box)(({theme}) => ({
  padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
  display: 'flex',
  alignItems: 'flex-start',
  whiteSpace: 'nowrap',
}));

// Primary value text — 16px/regular. Color supplied via sx.
export const ValueText = styled(Typography)(() => ({
  fontSize: 16,
  lineHeight: '20px',
  fontWeight: 400,
  whiteSpace: 'nowrap',
}));

// Grey remainder next to value — 14px/regular, onSurfaceLight (always grey).
export const RemainderText = styled(Typography)(({theme}) => ({
  fontSize: 14,
  lineHeight: '20px', // vertically aligned with ValueText baseline
  fontWeight: 400,
  color: theme.palette.semantic_dark?.surface.onSurfaceLight,
  whiteSpace: 'nowrap',
}));
